import _ from "lodash";
import React, { Fragment, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Card, CardBody, Col, Row } from "reactstrap";
import NumberFormat from "react-number-format";
import { toast } from "react-toastify";
import { useGetItemInfo, useGetSampleOrProductionSetup } from "../hooks";
import { DesktopContainer } from "../layouts";
import { api, constants, dateHelpers, helpers, reportHelpers, UserContext } from "../utils";
import {
   AttachmentManager,
   DynamicInput,
   InventoryDashboardCurrInvGrid,
   InventoryDashboardCustomersGrid,
   InventoryDashboardInvAdjGrid,
   InventoryDashBoardInvTraGrid,
   InventoryDashboardInvVarGrid,
   InventoryDashBoardPMNeedGrid,
   InventoryDashboardPurActGrid,
   InventoryDashboardSalActGrid,
   InventoryDashBoardWhereUsed,
   ItemTypeahead,
   ServiceThreadSpinner,
} from "./";
import useCookies from "react-use-cookie";

const sectionHeadersCopy = constants.InventoryDashboardSectionHeaders;
const switchBoardCopy = constants.WITH_ROLES_INVENTORYDASHBOARD_SWITCHES;

export default function InventoryDashboard(props) {
   const navigate = useNavigate();
   const location = useLocation();
   const { currentUser } = useContext(UserContext);
   const { itemNumber } = useParams();
   const [cookies, setCookie] = useCookies(constants.LocalStorageKeys.InventoryDashboard, null);
   const [controlItem, setControlItem] = useState({
      ...constants.SORTING_STATE_INVENTORY_DASHBOARD,
      ...location.state,
   });
   const [editing, setEditing] = useState(false);
   const [item, setItem] = useState(() => {
      const item = localStorage.getItem("itemNumber");
      const initialValue = JSON.parse(item);
      return initialValue || "";
   });
   const [refreshScreen, setRefreshscreen] = useState(false);
   const [roles, setRoles] = useState([]);
   const [saving, setSaving] = useState(false);
   const [screenSwitches, setScreenSwitches] = useState(_.cloneDeep(constants.WITH_ROLES_INVENTORYDASHBOARD_SCREENSWITCHES));
   const [sectionHeaders, setSectionHeaders] = useState(_.cloneDeep(sectionHeadersCopy));
   const [stdCostInput, setStdCostInput] = useState(null);
   const [switchBoard, setSwitchBoard] = useState(_.cloneDeep(switchBoardCopy));
   const itemGeneral = useGetItemInfo(controlItem);
   const itemSampleOrProdRequest = useGetSampleOrProductionSetup(controlItem);

   useEffect(() => {
      let foundCookies = cookies;
      if (foundCookies && foundCookies !== "null") {
         let parsedCookies = JSON.parse(foundCookies);

         // This is temporary because we are adding a new tab to the dashboard
         // Once this propagates to all users we can remove this if block
         // If the cookie doesn't have it then insert PM Needs right after Purchasing Activity in the cookie array
         if (!foundCookies.includes("purchasingNeeds")) {
            parsedCookies.splice(3, 0, {"name":"PM NEEDS","type":9,"label":"purchasingNeeds","value":true,"col":1,"rightSide":true,"colSize":"1","accesss":[1,10,13,14],"inventoryButtonValue":false});
         }

         _.each(parsedCookies, (opt) => {
            if (opt.name === "General Information") {
               opt.name = "General";
            }
            if (opt.name === "CURRENT INVENTORY") {
               opt.name = "INVENTORY";
            }
            if (opt.name === "PURCHASING ACTIVITY") {
               opt.name = "PURCHASING";
            }
            if (opt.name === "SALES ACTIVITY") {
               opt.name = "SALES";
            }
            if (opt.name === "INVENTORY TRANSFERS") {
               opt.name = "TRANSFERS";
            }
            if (opt.name === "INVENTORY VARIANCE") {
               opt.name = "VARIANCES";
            }
            if (opt.name === "INVENTORY ADJUSTMENTS") {
               opt.name = "ADJUSTMENTS";
            }
         });

         setSwitchBoard(parsedCookies);
         let tempScreenSwitches = screenSwitches;
         _.each(parsedCookies, (opt) => {
            if (opt.inventoryButtonValue) {
               tempScreenSwitches[opt.label].showing = !tempScreenSwitches[opt.label].showing;
            }
         });
         setScreenSwitches(tempScreenSwitches);
         setRefreshscreen(!refreshScreen);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      if (!currentUser) {
         setRoles([]);
      } else {
         let roleList = _.map(currentUser.roles, (r) => r.typeOfUserRole);
         setRoles(roleList);
      }
   }, [currentUser]);

   useEffect(() => {
      if (!_.isEmpty(itemGeneral.item)) {
         _.map(sectionHeaders, (obj) => {
            if (obj.label === "averageLeadDays") {
               obj.value = `${_.round(itemGeneral.item[obj.label] / 100)} Days`;
            } else if (obj.label === "alternateItem1") {
               obj.value = !itemGeneral.item["altItem1WithDesc"]?.length
                  ? ""
                  : `${itemGeneral.item["altItem1WithDesc"]} - ${_.round(itemGeneral.item["altItem1QtyOnHand"], 0)} on hand`;
            } else if (obj.label === "alternateItem2") {
               obj.value = !itemGeneral.item["altItem2WithDesc"]?.length
                  ? ""
                  : `${itemGeneral.item["altItem2WithDesc"]} - ${_.round(itemGeneral.item["altItem2QtyOnHand"])} on hand`;
            } else if (obj.label === "qcHold") {
               obj.value = itemGeneral.item["isQCHold"]
                  ? (<span className="text-danger">YES {helpers.redX()}</span>)
                  : "NO";
            } else {
               obj.value = itemGeneral.item[obj.label];
            }
            return obj;
         });
         setRefreshscreen(!refreshScreen);
      }
      else if (itemGeneral.success && !itemGeneral.loading && controlItem.selectedItem?.value) {
         // The lookup found nothing - an unknown or inactive item, typically opened from a link. Clear the previous item's
         // details: left on screen, Edit pre-filled THAT item's standard cost and Save sent it under this item number.
         setEditing(false);
         setSectionHeaders(_.cloneDeep(sectionHeadersCopy));
         toast.error(`Item ${controlItem.selectedItem.value} was not found or is inactive.`);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [itemGeneral.success, itemGeneral.loading]);

   useEffect(() => {
      document.title = `Inventory Dashboard`;
      if (itemNumber) {
         const json = decodeURI(itemNumber);
         const opt = JSON.parse(json);
         setControlItem({
            ...controlItem,
            selectedItem: opt,
         });
      } else if (item) {
         setControlItem({
            ...controlItem,
            selectedItem: item,
         });
      } else {
         if (location.state !== controlItem.selectedItem) {
            setControlItem({
               ...controlItem,
               ...location.state,
            });
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [location]);

   function editCosts() {
      let standardCostHeader = _.find(sectionHeaders, x => x.label === "standardCost");
      if (standardCostHeader?.value) {
         setStdCostInput(standardCostHeader.value);
      }
      setEditing(true);
   }

   function handleTypeahead(opt) {
      if (item) {
         setItem("");
         localStorage.removeItem("itemNumber");
      }
      if (editing) {
         setEditing(false);
      }
      if (_.isNil(opt)) {
         navigate(constants.ENDPOINTS.inventoryDashboard, {
            state: { selectedItem: null },
            replace: true,
         });
      } else {
         setSectionHeaders(_.cloneDeep(sectionHeadersCopy));
         navigate(constants.ENDPOINTS.inventoryDashboard, {
            state: { selectedItem: opt },
            replace: true,
         });
      }
   }

   const onChange = (fieldName, _fieldValue, idx, _texValue, isRegularCheckBox) => {
      if (isRegularCheckBox) {
         switchBoard[idx].value = !switchBoard[idx].value;
         switchBoard[idx].inventoryButtonValue = !switchBoard[idx].value;
         setScreenSwitches((prev) => {
            prev[fieldName].showing = !prev[fieldName].showing;
            return prev;
         });
         let switchList = [];
         _.each(switchBoard, (sw) => {
            switchList.push(_.cloneDeep(sw));
         });
         saveSearchCookie(JSON.stringify(switchList));
         setRefreshscreen(!refreshScreen);
         return;
      }
   };

   const onSortChange = (state) => {
      const Individual_State = {
         ...state,
         getAll: false,
      };

      setControlItem({
         ...controlItem,
         ...Individual_State,
      });
   };

   const PriceHistory = () => (
      <Col>
         <Button className="shredButton">
            <a rel="noreferrer"
               href={`/pm-price-history/${controlItem.selectedItem.value}`}
               target="_blank"
               className="regularAnchor text-white"
            >
               PM Price History
            </a>
         </Button>
      </Col>
   )

   function saveCosts() {
      if (saving) return;
      // selectedItem.value is the item number however the dashboard was opened - a picked option, the item-menu link
      // or a {value, label} link (which has no itemNumber field) - and it is always the CURRENT selection. The reloaded
      // record (itemGeneral) is not used: it is empty for an item GetItemInfo does not return (an inactive item).
      const selectedItemNumber = _.trim(controlItem.selectedItem?.value || "");
      if (itemGeneral.loading) {
         // Backstop - the Edit button sits inside the loading spinner, so this is not normally reachable.
         toast.error("The item is still loading. Wait a moment, then save again.");
         return;
      }
      if (!stdCostInput && stdCostInput !== 0) {
         toast.error("Invalid standard cost.");
         return;
      }
      // escape the forward slash character or pound symbol if in the item so it won't mess up the URL
      // Example = MP.11/64S40007 this causes the url to look like it has an extra parameter because
      // of the / character so the URL is broken and a Not Found error is thrown.
      const itemNumber = selectedItemNumber.replace(/\//g, '%2F').replace(/#/g, '%23');
      if (!itemNumber.length) {
         toast.error("Could not find item number.");
         return;
      }
      setSaving(true);
      api.fetch(`Inventory/UpdateInventoryItemMaster`, {
         itemNumber: itemNumber,
         standardCost: stdCostInput
      })
         .then((response) => {
            if (response.data && response.data.success) {
               toast.success("Save successful.");
               setEditing(false);
               setSectionHeaders(_.cloneDeep(sectionHeadersCopy));
               navigate(constants.ENDPOINTS.inventoryDashboard, {
                  state: { selectedItem: controlItem.selectedItem },
                  replace: true,
               });
            }
            else {
               toast.error(response.data.message || "Save failed.");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setSaving(false);
         });
   }

   function saveSearchCookie(data) {
      setCookie(data, {
         // path: location.pathname,
         domain: window.location.hostname,
         sameSite: "Strict",
         days: 60,
         // maxAge: 60 * 60 * 24 * 60, //5 minutes
         // expires: dateHelpers.addDays(new Date(), 60),
      });
   }

   return (
      <DesktopContainer screenName={"Inventory Dashboard"}>
         <div className="filter-header stick-top">
            <Row className="justify-content-center mb-2">
               <Col xs="3" lg="1" className="text-end">
                  <h4 className="pt-1 text-white" children="Item" />
               </Col>
               <Col xs="6">
                  <ItemTypeahead
                     onChange={(e) => handleTypeahead(e)}
                     value={controlItem.selectedItem}
                  />
               </Col>
            </Row>
            <div>
               <Col className="InlineHeadings">
                  {_.map(switchBoard, (s, index) => {
                     if (helpers.hasRole(roles, s.accesss))
                        return (
                           <DynamicInput
                              key={`${index}-abc`}
                              obj={s}
                              idx={index}
                              onChangeCallback={onChange}
                           />
                        );
                  })}
               </Col>
            </div>
         </div>

         {screenSwitches.generalInformation.showing && controlItem.selectedItem !== null && (
            <>
               <Card className="dashboard-card">
                  <CardBody>
                     <div>
                        <span className="h2 me-2">General Information</span>
                     </div>
                     <hr />
                     <ServiceThreadSpinner on={itemGeneral.loading}>
                        <Row>
                           <div>
                              {/*{console.log("headers: ", sectionHeaders)}*/}
                              <Row className="mb-2">
                                 <Col xs="6">
                                    {_.map(sectionHeaders, (s) => {
                                       if (s.col === 1)
                                          if (s.label === "alternateItem1") {
                                             return (
                                                <Row key={`${s.displayName}-abt`}>
                                                   <Col xs="4">
                                                      <h5 children={`${s.displayName}`} className="float-start" />
                                                   </Col>
                                                   {s.value?.length ? (
                                                      <Col
                                                         className={s.className ? s.className : ""}
                                                         onClick={() =>
                                                            navigate(constants.ENDPOINTS.inventoryDashboard,
                                                               {
                                                                  state: {
                                                                     selectedItem: {
                                                                        label: itemGeneral.item?.altItem1WithDesc,
                                                                        value: itemGeneral.item?.alternateItem1,
                                                                     },
                                                                  },
                                                                  replace: true,
                                                               }
                                                            )
                                                         }
                                                      >
                                                         {s.value}
                                                      </Col>
                                                   ) : null}
                                                </Row>
                                             );
                                          } else if (s.label === "alternateItem2") {
                                             return (
                                                <Row key={`${s.displayName}-abt`}>
                                                   <Col xs="4">
                                                      <h5 children={`${s.displayName}`} className="float-start" />
                                                   </Col>
                                                   {s.value?.length ? (
                                                      <Col
                                                         className={s.className ? s.className : ""}
                                                         onClick={() =>
                                                            navigate(constants.ENDPOINTS.inventoryDashboard,
                                                               {
                                                                  state: {
                                                                     selectedItem: {
                                                                        label: itemGeneral.item?.altItem2WithDesc,
                                                                        value: itemGeneral.item?.alternateItem2,
                                                                     },
                                                                  },
                                                                  replace: true,
                                                               }
                                                            )
                                                         }
                                                      >
                                                         {s.value}
                                                      </Col>
                                                   ) : null}
                                                </Row>
                                             );
                                          } else if (s.label === "standardCost") {
                                             return (
                                                <Row key={`${s.displayName}-abt`}>
                                                   <Col xs="4">
                                                      <h5 children={`${s.displayName}`} className="float-start" />
                                                   </Col>
                                                   {s.value ? (
                                                      <Col className={s.className ? s.className : ""} >
                                                         {editing
                                                            ? (<NumberFormat
                                                               className={"form-control"}
                                                               value={stdCostInput}
                                                               onValueChange={({ floatValue }) => {
                                                                  setStdCostInput(floatValue);
                                                               }}
                                                               decimalScale={5}
                                                               allowNegative={true}
                                                            />)
                                                            : s.value}
                                                      </Col>
                                                   ) : null}
                                                </Row>
                                             );
                                          } else {
                                             return (
                                                <Row key={`${s.displayName}-abt`}>
                                                   <Col xs="4">
                                                      <h5 children={`${s.displayName}`} className="float-start" />
                                                   </Col>
                                                   <Col
                                                      className={s.className ? s.className : ""}
                                                      onClick={
                                                         s.onclick
                                                            ? () =>
                                                               reportHelpers.downloadSpecReportPdf(
                                                                  itemGeneral.item.itemNumber
                                                               )
                                                            : null
                                                      }
                                                   >
                                                      {s.value}
                                                   </Col>
                                                </Row>
                                             );
                                          }
                                    })}
                                 </Col>
                                 <Col>
                                    {_.map(sectionHeaders, (s) => {
                                       const vendorlink = s.label === "vendorIdWithName";
                                       if (s.col === 2) {
                                          return (
                                             <Row key={`${s.displayName}-abt`}>
                                                <Col xs="6">
                                                   <h5 children={`${s.displayName}`} className="float-start" />
                                                </Col>
                                                <Col
                                                   className={s.className ? s.className : ""}
                                                   children={
                                                      vendorlink ? (
                                                         <a rel="noreferrer"
                                                            href={`/vendor-dashboard/${JSON.stringify({
                                                                  label: itemGeneral.item?.vendorIdWithName,
                                                                  value: itemGeneral.item?.vendorId,
                                                               })}`}
                                                            target="_blank"
                                                            className="styledAnchor"
                                                         >
                                                            {s.value}
                                                         </a>
                                                      ) : (
                                                         s.value
                                                      )
                                                   }
                                                />
                                             </Row>
                                          );
                                       }
                                    })}
                                 </Col>
                              </Row>
                              <Row>
                                 <Col className="width135px">
                                    <Button className="shredButton" disabled={!controlItem.selectedItem}>
                                       <a rel="noreferrer"
                                          href={`/bom-components/${JSON.stringify({
                                             label: controlItem.selectedItem.label,
                                             value: controlItem.selectedItem.value,
                                          })}`}
                                          target="_blank"
                                          className="regularAnchor text-white"
                                       >
                                          View Bom
                                       </a>
                                    </Button>
                                 </Col>
                                 {_.startsWith(controlItem.selectedItem.value, "PM") ? <PriceHistory /> : null}
                                 {(currentUser?.isAdmin || currentUser?.isPowerUser || currentUser?.isInventoryEdit || currentUser?.isPurchasingEdit)
                                    && !_.isEmpty(itemGeneral.item)
                                    ? (editing
                                       ? (<Fragment>
                                          <Col>
                                             <Button
                                                className="shredButton buttonRight mx-2"
                                                disabled={saving}
                                                onClick={saveCosts}
                                             >
                                                Save
                                             </Button>
                                             <Button
                                                className="shredButton buttonRight"
                                                disabled={saving}
                                                onClick={() => setEditing(false)}
                                             >
                                                Cancel
                                             </Button>
                                          </Col>
                                       </Fragment>)
                                       : (<Col>
                                          <Button
                                             className="shredButton buttonRight"
                                             onClick={editCosts}
                                          >
                                             Edit
                                          </Button>
                                       </Col>))
                                    : null}
                              </Row>
                           </div>
                        </Row>
                     </ServiceThreadSpinner>
                  </CardBody>
               </Card>

               {controlItem.selectedItem !== null && !itemSampleOrProdRequest?.isLoading &&
                  itemSampleOrProdRequest?.header?.length > 1 && (
                     <Card className="dashboard-card">
                        <CardBody>
                           <div>
                              <span className="h2 me-2">
                                 {itemSampleOrProdRequest.header}
                              </span>
                           </div>
                           <hr />
                           <Row>
                              <Col>
                                 <Row>
                                    <Col xs="3"><h5 children={`Item Entered By:`} className="float-start" /></Col>
                                    <Col>{itemSampleOrProdRequest.item.itemSetupBy} on {dateHelpers.toLongDateString(itemSampleOrProdRequest.item.itemSetupDate)}</Col>
                                 </Row>
                                 <Row>
                                    <Col xs="3"><h5 children={`Specification Entered By:`} className="float-start" /></Col>
                                 <Col>{itemSampleOrProdRequest.item.specSetupBy} on {dateHelpers.toLongDateString(itemSampleOrProdRequest.item.specSetupDate)}</Col>
                                 </Row>
                                 {itemSampleOrProdRequest.item.machineSetupBy &&
                                    itemSampleOrProdRequest.item.machineSetupBy.length !== 0 ? (
                                    <Row>
                                       <Col xs="3"><h5 children={`MSR(s) Entered By:`} className="float-start" /></Col>
                                       <Col>{itemSampleOrProdRequest.item.machineSetupBy} on {dateHelpers.toLongDateString(itemSampleOrProdRequest.item.machineSetupDate)}</Col>
                                    </Row>
                                 ) : (
                                    <Row>
                                       <Col xs="3"><h5 children={`MSR(s) Entered By:`} className="float-start" /></Col>
                                       <Col>Not Applicable to this Item</Col>
                                    </Row>
                                 )}
                                 {itemSampleOrProdRequest.item.bomSetupBy &&
                                    itemSampleOrProdRequest.item.bomSetupBy.length !== 0 ? (
                                    <Row>
                                       <Col xs="3"><h5 children={`BOM Entered By:`} className="float-start" /></Col>
                                       <Col>{itemSampleOrProdRequest.item.bomSetupBy} on {dateHelpers.toLongDateString(itemSampleOrProdRequest.item.bomSetupDate)}</Col>
                                    </Row>
                                 ) : (
                                    <Row>
                                       <Col xs="3"><h5 children={`BOM Entered By:`} className="float-start" /></Col>
                                       <Col>Not Applicable to Purchased Material</Col>
                                    </Row>
                                 )}
                                 {itemSampleOrProdRequest.header === "Production Setup" ? (
                                    <Row>
                                       <Col xs="3"><h5 children={`Final Approval By:`} className="float-start" /></Col>
                                       <Col>{itemSampleOrProdRequest.item.approvedBy} on {dateHelpers.toLongDateString(itemSampleOrProdRequest.item.approvedDate)}</Col>
                                    </Row>
                                 ) : (
                                    <Row>
                                       <Col xs="3"><h5 children={`Sample Promoted By:`} className="float-start" /></Col>
                                       <Col>{itemSampleOrProdRequest.item.promotedBy} on {dateHelpers.toLongDateString(itemSampleOrProdRequest.item.promotedDate)}</Col>
                                    </Row>
                                 )}
                              </Col>
                           </Row>
                        </CardBody>
                     </Card>
                  )}

               {itemGeneral !== null && screenSwitches.generalInformation.showing ? (
                  <Card className="dashboard-card">
                     <CardBody>
                        <AttachmentManager
                           attachmentTypeId={constants.ATTACHMENT_TYPES.Item}
                           contextName={itemGeneral.item?.ddlValue ? itemGeneral.item.ddlValue : itemGeneral?.itemObj?.ddlValue}
                           attachmentKey={itemGeneral.item?.ddlValue ? itemGeneral.item.ddlValue : itemGeneral?.itemObj?.ddlValue}
                        />
                     </CardBody>
                  </Card>
               ) : null}
            </>
         )}

         {/* { screenSwitches && */}
         <>
            {screenSwitches.currentInventory.showing &&
               controlItem.selectedItem !== null &&
               roles.length !== 0 &&
               helpers.hasRole(roles, screenSwitches.currentInventory.access) && (
                  <InventoryDashboardCurrInvGrid
                     params={controlItem}
                     sortCallback={onSortChange}
                     sortField={controlItem.currentInvSortField}
                     sortDirection={controlItem.currentInvSortDirection}
                     isLotTracked={itemGeneral.item?.isLotTracked}
                     onUpdateRow={onSortChange}
                     roles={roles}
                  />
               )}

            {screenSwitches.customers.showing &&
               controlItem.selectedItem !== null &&
               roles.length !== 0 &&
               helpers.hasRole(roles, screenSwitches.customers.access) && (
                  <InventoryDashboardCustomersGrid
                     params={controlItem}
                     sortCallback={onSortChange}
                     sortField={controlItem.customersSortField}
                     sortDirection={controlItem.customersSortDirection}
                     roles={roles}
                  />
               )}

            {screenSwitches.inventoryAdjustments.showing &&
               controlItem.selectedItem !== null &&
               roles.length !== 0 &&
               helpers.hasRole(roles, screenSwitches.inventoryAdjustments.access) && (
                  <InventoryDashboardInvAdjGrid
                     params={controlItem}
                     onRadButtonCall={onSortChange}
                     roles={roles}
                  />
               )}

            {screenSwitches.inventoryTransfers.showing &&
               controlItem.selectedItem !== null &&
               roles.length !== 0 &&
               helpers.hasRole(roles, screenSwitches.inventoryTransfers.access) && (
                  <InventoryDashBoardInvTraGrid
                     params={controlItem}
                     sortCallback={onSortChange}
                     sortField={controlItem.invTraSortField}
                     sortDirection={controlItem.invTraSortDirection}
                     roles={roles}
                  />
               )}

            {screenSwitches.inventoryVariance.showing &&
               controlItem.selectedItem !== null &&
               roles.length !== 0 &&
               helpers.hasRole(roles, screenSwitches.inventoryVariance.access) && (
                  <InventoryDashboardInvVarGrid
                     params={controlItem}
                     sortCallback={onSortChange}
                     sortField={controlItem.invVarSortField}
                     sortDirection={controlItem.invVarSortDirection}
                     roles={roles}
                  />
               )}

            {screenSwitches.purchasingNeeds.showing &&
               controlItem.selectedItem !== null &&
               roles.length !== 0 &&
               helpers.hasRole(roles, screenSwitches.purchasingNeeds.access) && (
                  <InventoryDashBoardPMNeedGrid
                     params={controlItem}
                     roles={roles}
                  />
               )}

            {screenSwitches.purchasingActivity.showing &&
               controlItem.selectedItem !== null &&
               roles.length !== 0 &&
               helpers.hasRole(roles, screenSwitches.purchasingActivity.access) && (
                  <InventoryDashboardPurActGrid
                     params={controlItem}
                     sortCallback={onSortChange}
                     sortField={controlItem.purchActivitySortField}
                     sortDirection={controlItem.purchActivitySortDirection}
                     roles={roles}
                  />
               )}

            {screenSwitches.salesActivity.showing &&
               controlItem.selectedItem !== null &&
               roles.length !== 0 &&
               helpers.hasRole(roles, screenSwitches.salesActivity.access) && (
                  <InventoryDashboardSalActGrid
                     params={controlItem}
                     roles={roles}
                  />
               )}

            {screenSwitches.whereUsed.showing &&
               controlItem.selectedItem !== null &&
               roles.length !== 0 &&
               helpers.hasRole(roles, screenSwitches.whereUsed.access) && (
                  <InventoryDashBoardWhereUsed
                     componentNumber={itemGeneral.item?.ddlValue}
                     roles={roles}
                  />
               )}
         </>
      </DesktopContainer>
   );
}
